import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { supabase } from "../../../../src/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ProfileImageUpload } from "@/components/ProfileImageUpload";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

interface MemberRole {
  role: string;
}

interface Member {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  role: string;
  region_id?: string;
  start_year: number;
  end_year?: number;
  profile_image_url?: string;
  notes?: string;
  order_index: number;
  is_active: boolean;
  regions?: { name: string };
  member_roles?: MemberRole[];
}

interface Region {
  id: string;
  name: string;
}

interface MemberManagerProps {
  userRole: string;
  userRegion?: string;
}

export const MemberManager: React.FC<MemberManagerProps> = ({ userRole, userRegion }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [nameFilter, setNameFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "",
    roles: [] as string[],
    region_id: "",
    start_year: new Date().getFullYear(),
    end_year: "",
    notes: "",
    order_index: 0,
    profile_image_url: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [roleDuplicateDialogOpen, setRoleDuplicateDialogOpen] = useState(false);
  const [duplicateRoleInfo, setDuplicateRoleInfo] = useState<{ role: string; existingMember: string } | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const roleGroups = [
    {
      groupName: "Executive Committee",
      roles: [
        { value: "president", label: "President" },
        { value: "vice_president", label: "Vice President" },
        { value: "secretary", label: "Secretary" },
        { value: "treasurer", label: "Treasurer" },
        { value: "prayer_secretary", label: "Prayer Secretary" },
      ]
    },
    {
      groupName: "Regional Coordinators",
      roles: [
        { value: "regional_president", label: "Regional President" },
        { value: "regional_vice_president", label: "Regional Vice President" },
        { value: "regional_secretary", label: "Regional Secretary" },
        { value: "regional_treasurer", label: "Regional Treasurer" },
        { value: "regional_joint_secretary", label: "Regional Joint Secretary" },
      ]
    },
    {
      groupName: "Women's Ministry",
      roles: [
        { value: "ministry_facilitator", label: "Ministry Facilitator" },
        { value: "prayer_facilitator", label: "Prayer Facilitator" },
      ]
    },
    {
      groupName: "Other",
      roles: [
        { value: "national_council", label: "National Council" },
      ]
    }
  ];

  const roleOptions = roleGroups.flatMap(group => group.roles);

  useEffect(() => {
    fetchMembers();
    fetchRegions();
  }, [selectedYear, userRole, userRegion]);

  const fetchMembers = async () => {
    try {
      let query = supabase
        .from("members")
        .select("*, regions(name), member_roles(role)")
        .order("order_index", { ascending: true });

      // Apply role-based filtering
      if (userRole === "regional_admin" && userRegion) {
        query = query.eq("region_id", userRegion);
      }

      // Filter by selected year - show members who were active during that year (including current year)
      const currentYear = new Date().getFullYear();
      console.log("Fetching members with filters:", {
        selectedYear,
        currentYear,
        userRole,
        userRegion,
      });

      if (selectedYear === currentYear) {
        // For current year, show ALL active members (no end_year OR end_year >= current year)
        // AND whose start_year is not in the future
        query = query.or(`end_year.is.null,end_year.gte.${currentYear}`).lte("start_year", currentYear);
      } else {
        // For past years, use the existing logic
        query = query.or(
          `and(start_year.lte.${selectedYear},end_year.gte.${selectedYear}),and(start_year.lte.${selectedYear},end_year.is.null)`,
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching members:", error);
        throw error;
      }

      console.log("Fetched members:", data?.length, "total");
      console.log("Regional coordinators:", data?.filter((m) => m.role?.includes("regional_")).length);

      // Sort members: president first, then others
      const sortedMembers = (data || []).sort((a, b) => {
        if (a.role === "president" && b.role !== "president") return -1;
        if (b.role === "president" && a.role !== "president") return 1;
        return a.order_index - b.order_index;
      });

      setMembers(sortedMembers);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast({
        title: "Error",
        description: "Failed to fetch members",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const { data, error } = await supabase.from("regions").select("id, name").order("name");

      if (error) throw error;
      setRegions(data || []);
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.full_name?.trim()) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.roles.length === 0) {
      toast({
        title: "Validation Error",
        description: "At least one role is required",
        variant: "destructive",
      });
      return;
    }

    // For regional roles, region is required
    const regionalRoles = [
      "regional_president",
      "regional_vice_president",
      "regional_secretary",
      "regional_joint_secretary",
      "regional_treasurer",
    ];
    const hasRegionalRole = formData.roles.some((role) => regionalRoles.includes(role));
    if (hasRegionalRole && !formData.region_id) {
      toast({
        title: "Validation Error",
        description: "Region is required for regional roles",
        variant: "destructive",
      });
      return;
    }

    // Check for duplicate roles (unless user already confirmed)
    if (!pendingSubmit && !editingMember) {
      const duplicateCheck = await checkForDuplicateRoles();
      if (duplicateCheck) {
        setDuplicateRoleInfo(duplicateCheck);
        setRoleDuplicateDialogOpen(true);
        return;
      }
    }

    // Reset pending submit flag
    setPendingSubmit(false);

    await performSubmit();
  };

  const checkForDuplicateRoles = async () => {
    // Define unique roles
    const uniqueNationalRoles = ['president', 'vice_president', 'secretary', 'treasurer', 'prayer_secretary'];
    const uniqueRegionalRoles = ['regional_president', 'regional_vice_president', 'regional_secretary', 'regional_joint_secretary', 'regional_treasurer'];
    
    // Check each role
    for (const role of formData.roles) {
      let query = supabase
        .from('members')
        .select('full_name, role, region_id, member_roles(role)')
        .eq('is_active', true);

      // Check if this is a unique role
      const isUniqueNational = uniqueNationalRoles.includes(role);
      const isUniqueRegional = uniqueRegionalRoles.includes(role);

      if (!isUniqueNational && !isUniqueRegional) {
        // National council and other non-unique roles can have duplicates
        continue;
      }

      // For regional roles, check within the same region
      if (isUniqueRegional && formData.region_id) {
        query = query.eq('region_id', formData.region_id);
      }

      const { data: existingMembers, error } = await query;

      if (error) {
        console.error('Error checking for duplicates:', error);
        continue;
      }

      // Check if any member has this role
      const memberWithRole = existingMembers?.find(m => {
        const memberRoles = m.member_roles?.map(mr => mr.role) || [m.role];
        return memberRoles.includes(role);
      });

      if (memberWithRole) {
        const roleName = roleOptions.find(r => r.value === role)?.label || role;
        return {
          role: roleName,
          existingMember: memberWithRole.full_name
        };
      }
    }

    return null;
  };

  const performSubmit = async () => {

    try {
      const memberData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        role: formData.roles[0], // Keep first role as primary for backward compatibility
        region_id: formData.region_id || null,
        start_year: formData.start_year,
        end_year: formData.end_year && formData.end_year !== "none" ? parseInt(formData.end_year) : null,
        notes: formData.notes,
        order_index: formData.order_index,
        profile_image_url: formData.profile_image_url,
      };

      let memberId: string;

      if (editingMember) {
        // Check if user can edit this member
        const canEdit =
          userRole === "global_admin" ||
          (userRole === "president" && editingMember.role === "president" && !editingMember.end_year);

        if (!canEdit) {
          toast({
            title: "Permission Denied",
            description: "You don't have permission to edit this member",
            variant: "destructive",
          });
          return;
        }

        const { error } = await supabase.from("members").update(memberData).eq("id", editingMember.id);
        if (error) throw error;
        memberId = editingMember.id;

        // Delete existing roles
        await supabase.from("member_roles").delete().eq("member_id", memberId);
      } else {
        const { data, error } = await supabase.from("members").insert([memberData]).select().single();
        if (error) throw error;
        memberId = data.id;
      }

      // Insert all selected roles
      const roleInserts = formData.roles.map((role) => ({
        member_id: memberId,
        role: role,
      }));

      const { error: rolesError } = await supabase.from("member_roles").insert(roleInserts);
      if (rolesError) throw rolesError;

      toast({
        title: "Success",
        description: editingMember ? "Member updated successfully" : "Member created successfully",
      });
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error("Error saving member:", error);
      toast({
        title: "Error",
        description: "Failed to save member",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDuplicateRole = () => {
    setPendingSubmit(true);
    setRoleDuplicateDialogOpen(false);
    // Trigger submit again with pending flag set
    performSubmit();
  };

  const openDeleteDialog = (member: Member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!memberToDelete) return;

    try {
      const { error } = await supabase.from("members").delete().eq("id", memberToDelete.id);

      if (error) throw error;
      toast({ title: "Success", description: "Member deleted successfully" });
      fetchMembers();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast({
        title: "Error",
        description: "Failed to delete member",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    }
  };

  const getInitials = (name?: string | null) => {
    if (!name || typeof name !== "string") {
      console.warn("[admin-members] Missing member full_name for initials", { name });
      return "?";
    }
    return name
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      role: "",
      roles: [],
      region_id: "",
      start_year: new Date().getFullYear(),
      end_year: "",
      notes: "",
      order_index: 0,
      profile_image_url: "",
    });
    setEditingMember(null);
    setIsDialogOpen(false);
  };

  const openEditDialog = (member: Member) => {
    setEditingMember(member);
    const memberRoles = member.member_roles?.map((mr) => mr.role) || [member.role];
    setFormData({
      full_name: member.full_name,
      email: member.email || "",
      phone: member.phone || "",
      role: member.role,
      roles: memberRoles,
      region_id: member.region_id || "",
      start_year: member.start_year,
      end_year: member.end_year?.toString() || "",
      notes: member.notes || "",
      order_index: member.order_index,
      profile_image_url: member.profile_image_url || "",
    });
    setIsDialogOpen(true);
  };

  const canAddMembers = userRole === "global_admin" || userRole === "Global Admin";
  const canEditMember = (member: Member) => {
    return (
      userRole === "global_admin" ||
      userRole === "Global Admin" ||
      (userRole === "president" && member.role === "president" && !member.end_year)
    );
  };

  // Filter members based on filters
  const filteredMembers = members.filter(member => {
    const matchesName = member.full_name.toLowerCase().includes(nameFilter.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter || 
      member.member_roles?.some(mr => mr.role === roleFilter);
    const matchesRegion = regionFilter === 'all' || member.region_id === regionFilter;
    return matchesName && matchesRole && matchesRegion;
  });

  const isGlobalAdmin = userRole === "global_admin" || userRole === "Global Admin";

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading members...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">Members</h2>
          <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canAddMembers && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setIsDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingMember ? "Edit Member" : "Add New Member"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Roles (select multiple)</Label>
                  <div className="space-y-4 max-h-96 overflow-y-auto border rounded-md p-3">
                    {roleGroups.map((group) => (
                      <div key={group.groupName}>
                        <h4 className="font-semibold text-sm text-primary mb-2 border-b pb-1">
                          {group.groupName}
                        </h4>
                        <div className="space-y-2 ml-2">
                          {group.roles.map((role) => (
                            <div key={role.value} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`role-${role.value}`}
                                checked={formData.roles.includes(role.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      roles: [...formData.roles, role.value],
                                      role: formData.roles.length === 0 ? role.value : formData.role,
                                    });
                                  } else {
                                    const newRoles = formData.roles.filter((r) => r !== role.value);
                                    setFormData({ ...formData, roles: newRoles, role: newRoles[0] || "" });
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <Label htmlFor={`role-${role.value}`} className="text-sm font-normal cursor-pointer">
                                {role.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.roles.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.roles.map((role) => (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {roleOptions.find((r) => r.value === role)?.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Region Selector - Always visible, required only for regional coordinator roles */}
                <div>
                  <Label htmlFor="region_id">
                    Region
                    {formData.roles.some((role) =>
                      [
                        "regional_president",
                        "regional_vice_president",
                        "regional_secretary",
                        "regional_joint_secretary",
                        "regional_treasurer",
                      ].includes(role)
                    ) && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <Select
                    value={formData.region_id || "none"}
                    onValueChange={(value) =>
                      setFormData({ ...formData, region_id: value === "none" ? "" : value })
                    }
                  >
                    <SelectTrigger id="region_id">
                      <SelectValue placeholder="Select region (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>

                      {regions.map((region) => (
                        <SelectItem key={region.id} value={region.id}>
                          {region.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.roles.some((role) =>
                    [
                      "regional_president",
                      "regional_vice_president",
                      "regional_secretary",
                      "regional_joint_secretary",
                      "regional_treasurer",
                    ].includes(role)
                  ) ? (
                    <p className="text-xs text-destructive mt-1">Required for regional coordinator roles</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">Optional - specify if member is associated with a specific region</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_year">Start Year</Label>
                    <Select
                      value={formData.start_year.toString()}
                      onValueChange={(value) => setFormData({ ...formData, start_year: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select start year" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: new Date().getFullYear() - 1998 },
                          (_, i) => new Date().getFullYear() - i,
                        ).map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="end_year">End Year (optional)</Label>
                    <Select
                      value={formData.end_year}
                      onValueChange={(value) => setFormData({ ...formData, end_year: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select end year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No end year</SelectItem>
                        {Array.from(
                          { length: new Date().getFullYear() - (formData.start_year - 1) },
                          (_, i) => new Date().getFullYear() - i,
                        )
                          .filter((year) => year >= formData.start_year)
                          .map((year) => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.roles.includes("president") && !formData.end_year && (
                  <div>
                    <Label htmlFor="notes">President's Message</Label>
                    <RichTextEditor
                      value={formData.notes}
                      onChange={(value) => setFormData({ ...formData, notes: value })}
                      placeholder="Welcome message from the president..."
                    />
                  </div>
                )}

                <div>
                  <ProfileImageUpload
                    currentImageUrl={formData.profile_image_url}
                    onImageUploaded={(url) => setFormData({ ...formData, profile_image_url: url })}
                    className="mt-2"
                    skipAuthCheck={true}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit">{editingMember ? "Update" : "Create"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters Section */}
      <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="name-filter" className="text-xs mb-1 block">Search by Name</Label>
          <Input
            id="name-filter"
            placeholder="Search by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
        <div className="min-w-[200px]">
          <Label htmlFor="role-filter" className="text-xs mb-1 block">Filter by Role</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger id="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roleOptions.map(role => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isGlobalAdmin && (
          <div className="min-w-[200px]">
            <Label htmlFor="region-filter" className="text-xs mb-1 block">Filter by Region</Label>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger id="region-filter">
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-8">
        {/* Executive Committee Section */}
        {(() => {
          const ecRoles = ["president", "vice_president", "secretary", "treasurer", "prayer_secretary"];
          const ecMembers = filteredMembers
            .filter((m) => ecRoles.includes(m.role))
            .sort((a, b) => ecRoles.indexOf(a.role) - ecRoles.indexOf(b.role));

          if (ecMembers.length === 0) return null;

          const president = ecMembers.find((m) => m.role === "president" && !m.end_year);
          const otherEC = ecMembers.filter((m) => !(m.role === "president" && !m.end_year));

          return (
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 rounded-lg transition-colors group border-b mb-4">
                <h3 className="text-xl font-semibold text-primary">Executive Committee</h3>
                <ChevronDown className="h-5 w-5 text-primary transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4">

              {/* President Card - Full Width */}
              {president && (
                <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 relative">
                  <CardHeader className="pb-4">
                    {/* Absolute positioned action buttons */}
                    {canEditMember(president) && (
                      <div className="absolute top-4 right-4 flex gap-2 z-10">
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(president)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {userRole === "global_admin" && (
                          <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(president)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    )}

                    <div className="flex items-start gap-4 pr-20">
                      <Avatar className="h-16 w-16 flex-shrink-0">
                        <AvatarImage src={president.profile_image_url} alt={president.full_name} />
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                          {getInitials(president.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-lg">{president.full_name}</h4>
                        {president.email && <p className="text-xs text-muted-foreground mt-1">{president.email}</p>}
                        {president.phone && <p className="text-xs text-muted-foreground">{president.phone}</p>}
                        <p className="text-xs text-muted-foreground mt-1">
                          {president.start_year} -{" "}
                          {president.end_year === new Date().getFullYear()
                            ? "Present"
                            : president.end_year || "Present"}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {president.notes && (
                      <Collapsible defaultOpen={false}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 rounded-lg transition-colors group">
                          <h5 className="text-sm font-semibold text-primary">President's Message</h5>
                          <ChevronDown className="h-4 w-4 text-primary transition-transform group-data-[state=open]:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 p-4 bg-muted/50 rounded-lg border">
                            <div
                              className="text-sm leading-relaxed prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(president.notes),
                              }}
                            />
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Other EC Members Grid */}
              {otherEC.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 items-stretch">
                  {otherEC.map((member) => (
                    <Card key={member.id} className="h-full min-h-[200px] transition-all duration-200 hover:shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-16 w-16 shrink-0">
                            <AvatarImage src={member.profile_image_url} alt={member.full_name} />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-lg">{member.full_name}</h4>
                            <p className="text-sm text-muted-foreground capitalize">{member.role.replace("_", " ")}</p>
                            {member.email && <p className="text-xs text-muted-foreground mt-1">{member.email}</p>}
                            {member.phone && <p className="text-xs text-muted-foreground">{member.phone}</p>}
                            <p className="text-xs text-muted-foreground mt-1">
                              {member.start_year} -{" "}
                              {member.end_year === new Date().getFullYear() ? "Present" : member.end_year || "Present"}
                            </p>
                          </div>

                          {canEditMember(member) && (
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(member)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {userRole === "global_admin" && (
                                <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(member)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              </CollapsibleContent>
            </Collapsible>
          );
        })()}

        {/* Regional Coordinators Section - Grouped by Region */}
        {(() => {
          const regionalRoles = [
            "regional_president",
            "regional_vice_president",
            "regional_secretary",
            "regional_joint_secretary",
            "regional_treasurer",
          ];

          // Get all members with regional roles
          const regionalCoordinators = filteredMembers.filter(
            (m) => m.member_roles?.some((mr) => regionalRoles.includes(mr.role)) || regionalRoles.includes(m.role),
          );

          // Group by region
          const coordinatorsByRegion = regionalCoordinators.reduce(
            (acc, member) => {
              const regionName = member.regions?.name || "Unknown Region";
              if (!acc[regionName]) acc[regionName] = [];
              acc[regionName].push(member);
              return acc;
            },
            {} as Record<string, Member[]>,
          );

          if (Object.keys(coordinatorsByRegion).length === 0) return null;

          return (
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 rounded-lg transition-colors group border-b mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-primary">Regional Coordinators</h3>
                  <p className="text-sm text-muted-foreground">
                    {regionalCoordinators.length} coordinator{regionalCoordinators.length !== 1 ? "s" : ""} across{" "}
                    {Object.keys(coordinatorsByRegion).length} region
                    {Object.keys(coordinatorsByRegion).length !== 1 ? "s" : ""}
                  </p>
                </div>
                <ChevronDown className="h-5 w-5 text-primary transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-6">

              {Object.entries(coordinatorsByRegion)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([regionName, coordinators]) => (
                  <Collapsible key={regionName} defaultOpen={true}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 rounded-lg transition-colors group mb-3">
                      <h4 className="text-lg font-medium text-muted-foreground flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {regionName}
                      </h4>
                      <ChevronDown className="h-4 w-4 text-primary transition-transform group-data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 items-stretch">
                      {coordinators
                        .sort((a, b) => {
                          // Sort by role order: president, vice president, secretary, joint secretary, treasurer
                          const roleOrderIndex = (role: string) => {
                            const order = {
                              regional_president: 0,
                              regional_vice_president: 1,
                              regional_secretary: 2,
                              regional_joint_secretary: 3,
                              regional_treasurer: 4,
                            };
                            return order[role as keyof typeof order] ?? 999;
                          };

                          const aRoles = a.member_roles?.map((mr) => mr.role) || [a.role];
                          const bRoles = b.member_roles?.map((mr) => mr.role) || [b.role];

                          const aMinOrder = Math.min(...aRoles.map(roleOrderIndex));
                          const bMinOrder = Math.min(...bRoles.map(roleOrderIndex));

                          if (aMinOrder !== bMinOrder) return aMinOrder - bMinOrder;

                          // If same role order, sort by order_index
                          if (a.order_index !== b.order_index) {
                            return (a.order_index || 999) - (b.order_index || 999);
                          }

                          // Finally, sort by name
                          return a.full_name.localeCompare(b.full_name);
                        })
                        .map((member) => {
                          const memberRolesList = member.member_roles?.map((mr) => mr.role) || [member.role];
                          const regionalRolesList = memberRolesList.filter((r) => regionalRoles.includes(r));

                          return (
                            <Card key={member.id} className="h-full min-h-[200px] transition-all duration-200 hover:shadow-md relative">
                              {canEditMember(member) && (
                                <div className="absolute top-3 right-3 flex gap-1 z-10">
                                  <Button size="sm" variant="ghost" onClick={() => openEditDialog(member)}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  {userRole === "global_admin" && (
                                    <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(member)}>
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              )}

                              <CardContent className="p-4 pr-20 sm:pr-24">
                                <div className="flex items-start gap-3">
                                  <Avatar className="h-12 w-12 flex-shrink-0">
                                    <AvatarImage src={member.profile_image_url} alt={member.full_name} />
                                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                                      {getInitials(member.full_name)}
                                    </AvatarFallback>
                                  </Avatar>

                                  <div className="flex-1 min-w-0 space-y-1.5">
                                    <div>
                                      <h4 className="font-semibold text-sm truncate">{member.full_name}</h4>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {regionalRolesList.map((role) => (
                                          <Badge key={role} variant="secondary" className="text-xs">
                                            {roleOptions.find((r) => r.value === role)?.label.replace("Regional ", "")}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="space-y-0.5">
                                      {member.email && (
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <Mail className="h-3 w-3 flex-shrink-0" />
                                          <span className="truncate">{member.email}</span>
                                        </div>
                                      )}
                                      {member.phone && (
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <Phone className="h-3 w-3 flex-shrink-0" />
                                          <span>{member.phone}</span>
                                        </div>
                                      )}
                                      <p className="text-xs text-muted-foreground">
                                        {member.start_year} -{" "}
                                        {member.end_year === new Date().getFullYear()
                                          ? "Present"
                                          : member.end_year || "Present"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                    </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })()}

        {/* Role Duplicate Confirmation Dialog */}
        <AlertDialog open={roleDuplicateDialogOpen} onOpenChange={setRoleDuplicateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Role Already Assigned</AlertDialogTitle>
              <AlertDialogDescription>
                {duplicateRoleInfo && (
                  <>
                    A member is already assigned to the <strong>{duplicateRoleInfo.role}</strong> role:{" "}
                    <strong>{duplicateRoleInfo.existingMember}</strong>. 
                    <br /><br />
                    Do you want to assign the same role to <strong>{formData.full_name}</strong> as well?
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setRoleDuplicateDialogOpen(false);
                setPendingSubmit(false);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDuplicateRole}>
                Yes, Assign Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* National Council Section */}
        {(() => {
          const ncMembers = filteredMembers
            .filter((m) => m.role === "national_council")
            .sort((a, b) => a.full_name.localeCompare(b.full_name));

          if (ncMembers.length === 0) return null;

          return (
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 rounded-lg transition-colors group border-b mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-primary">National Council</h3>
                  <p className="text-sm text-muted-foreground">
                    {ncMembers.length} member{ncMembers.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <ChevronDown className="h-5 w-5 text-primary transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4">

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                {ncMembers.map((member) => (
                  <Card key={member.id} className="h-fit transition-all duration-200 hover:shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16 shrink-0">
                          <AvatarImage src={member.profile_image_url} alt={member.full_name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {getInitials(member.full_name)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-lg">{member.full_name}</h4>
                          {member.email && <p className="text-xs text-muted-foreground mt-1">{member.email}</p>}
                          {member.phone && <p className="text-xs text-muted-foreground">{member.phone}</p>}
                          <p className="text-xs text-muted-foreground mt-1">
                            {member.start_year} -{" "}
                            {member.end_year === new Date().getFullYear() ? "Present" : member.end_year || "Present"}
                          </p>
                        </div>

                        {canEditMember(member) && (
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => openEditDialog(member)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {userRole === "global_admin" && (
                              <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(member)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })()}

        {/* Women's Ministry Section */}
        {(() => {
          const womensMinistryRoles = ["ministry_facilitator", "prayer_facilitator"];
          const womensMinistryMembers = filteredMembers
            .filter((m) => m.member_roles?.some((mr) => womensMinistryRoles.includes(mr.role)) || womensMinistryRoles.includes(m.role))
            .sort((a, b) => a.full_name.localeCompare(b.full_name));

          if (womensMinistryMembers.length === 0) return null;

          return (
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 rounded-lg transition-colors group border-b mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-primary">Women's Ministry</h3>
                  <p className="text-sm text-muted-foreground">
                    {womensMinistryMembers.length} member{womensMinistryMembers.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <ChevronDown className="h-5 w-5 text-primary transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4">

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 items-stretch">
                {womensMinistryMembers.map((member) => {
                  const memberRolesList = member.member_roles?.map((mr) => mr.role) || [member.role];
                  const wmRolesList = memberRolesList.filter((r) => womensMinistryRoles.includes(r));

                  return (
                    <Card key={member.id} className="h-full min-h-[200px] transition-all duration-200 hover:shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-16 w-16 shrink-0">
                            <AvatarImage src={member.profile_image_url} alt={member.full_name} />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-lg">{member.full_name}</h4>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {wmRolesList.map((role) => (
                                <Badge key={role} variant="secondary" className="text-xs">
                                  {roleOptions.find((r) => r.value === role)?.label}
                                </Badge>
                              ))}
                            </div>
                            {member.email && <p className="text-xs text-muted-foreground mt-1">{member.email}</p>}
                            {member.phone && <p className="text-xs text-muted-foreground">{member.phone}</p>}
                            <p className="text-xs text-muted-foreground mt-1">
                              {member.start_year} -{" "}
                              {member.end_year === new Date().getFullYear() ? "Present" : member.end_year || "Present"}
                            </p>
                          </div>

                          {canEditMember(member) && (
                            <div className="flex gap-1 shrink-0">
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(member)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {userRole === "global_admin" && (
                                <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(member)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })()}
      </div>

      {/* Custom Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{memberToDelete?.full_name}</strong>?
              <br />
              <span className="text-sm text-muted-foreground mt-1 block">
                Role: {memberToDelete?.role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {members.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No members found</p>
        </div>
      )}
    </div>
  );
};
